import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Edit, Star, Camera, X, DollarSign, TrendingUp, Brain, FileText, GraduationCap, Crown } from 'lucide-react';

const Profile = () => {
  const { id } = useParams();
  const { user: currentUser, setUser: setCurrentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    field_of_concentration: '',
    school: ''
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [studyDocs, setStudyDocs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);

  const isOwnProfile = currentUser && currentUser.id === id;

  useEffect(() => {
    fetchUser();
    fetchDocuments();
    if (isOwnProfile) {
      fetchSalesData();
      fetchStripeStatus();
      fetchStudyDocs();
      fetchCourses();
      fetchSubscriptionStatus();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${id}`);
      setUser(response.data);
      setFormData({
        name: response.data.name,
        bio: response.data.bio || '',
        field_of_concentration: response.data.field_of_concentration || '',
        school: response.data.school || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/');
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/user/${id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents');
    }
  };

  const fetchSalesData = async () => {
    try {
      const response = await axios.get(`${API}/sales`);
      setSalesData(response.data);
    } catch (error) {
      console.error('Failed to fetch sales data');
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/account-status`);
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status');
    }
  };

  const fetchStudyDocs = async () => {
    try {
      const response = await axios.get(`${API}/study-builder/documents`);
      const completed = (response.data.documents || []).filter(d => d.status === 'completed');
      setStudyDocs(completed);
    } catch (error) {
      console.error('Failed to fetch study documents');
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/by-seller/${id}`);
      setCourses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch courses');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/study-builder/subscription-status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Pro subscription? You will lose unlimited AI study generation.')) return;
    setCancelingSubscription(true);
    try {
      await axios.post(`${API}/study-builder/cancel-subscription`);
      toast.success('Subscription canceled');
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleSetupPayout = async () => {
    try {
      const response = await axios.post(`${API}/stripe/connect/onboarding-link`);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start payout setup');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${id}`, formData);
      toast.success('Profile updated!');
      setEditing(false);
      fetchUser();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };


  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/users/${id}/profile-picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Profile picture updated!');
      
      // Refresh profile and update current user context
      fetchUser();
      if (isOwnProfile) {
        const meResponse = await axios.get(`${API}/auth/me`);
        setCurrentUser(meResponse.data);
      }
      
      // Force refresh profile picture
      setProfilePictureUrl(`${API}/users/${id}/profile-picture?t=${Date.now()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${id}/profile-picture`);
      toast.success('Profile picture deleted');
      
      fetchUser();
      if (isOwnProfile) {
        const meResponse = await axios.get(`${API}/auth/me`);
        setCurrentUser(meResponse.data);
      }
      
      setProfilePictureUrl(null);
    } catch (error) {
      toast.error('Failed to delete profile picture');
    }
  };


  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
            <div className="flex items-center gap-4 md:gap-6">
              <div className="relative">
                <Avatar className="w-20 h-20 md:w-24 md:h-24">
                  {(user.profile_image || profilePictureUrl) ? (
                    <img 
                      src={profilePictureUrl || `${API}${user.profile_image}?t=${Date.now()}`}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-blue-600 to-blue-800 text-white text-2xl md:text-3xl">
                    {user.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {isOwnProfile && (
                  <div className="absolute bottom-0 right-0">
                    <input
                      type="file"
                      id="profile-picture-upload"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfilePictureUpload}
                      disabled={uploadingPicture}
                    />
                    <label
                      htmlFor="profile-picture-upload"
                      title="Upload profile picture (JPEG, PNG, WEBP, GIF - Max 5MB)"
                      className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg block transition-colors"
                    >
                      {uploadingPicture ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </label>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                    {user.name}
                  </h1>
                  {isOwnProfile && subscriptionStatus?.has_subscription && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
                      <Crown className="w-3 h-3" /> Pro
                    </span>
                  )}
                </div>
                {user.school && (
                  <p className="text-base md:text-lg text-gray-600 mb-1">{user.school}</p>
                )}
                {user.field_of_concentration && (
                  <p className="text-sm text-gray-500">{user.field_of_concentration}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isOwnProfile ? (
                <>
                  <Button
                    data-testid="edit-profile-btn"
                    onClick={() => setEditing(!editing)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {editing ? 'Cancel' : 'Edit Profile'}
                  </Button>
                  
                  {/* Payout/Earnings Button */}
                  {salesData && salesData.sales_count > 0 && (
                    <Button
                      onClick={() => navigate('/earnings')}
                      className="rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      {stripeStatus && stripeStatus.onboarding_complete 
                        ? `$${salesData.total_revenue.toFixed(2)} Earned`
                        : 'Setup Payouts'}
                    </Button>
                  )}
                </>
              ) : null}
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleUpdate} className="space-y-4 p-6 bg-blue-50/50 rounded-xl border border-blue-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <Input
                  data-testid="profile-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">School / Institution</label>
                <Input
                  data-testid="profile-school-input"
                  value={formData.school}
                  onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                  className="rounded-xl"
                  placeholder="e.g. Stanford University"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field of Study</label>
                <Input
                  data-testid="profile-field-input"
                  value={formData.field_of_concentration}
                  onChange={(e) => setFormData({ ...formData, field_of_concentration: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                <Textarea
                  data-testid="profile-bio-input"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  className="rounded-xl"
                  placeholder="Tell us about yourself..."
                />
              </div>
              {(user.profile_image || profilePictureUrl) && (
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    onClick={handleDeleteProfilePicture}
                    variant="outline"
                    className="rounded-xl text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Remove Profile Picture
                  </Button>
                </div>
              )}
              {subscriptionStatus?.has_subscription && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Pro Subscription</p>
                      <p className="text-xs text-gray-500">
                        Renews {subscriptionStatus.current_period_end
                          ? new Date(subscriptionStatus.current_period_end).toLocaleDateString()
                          : 'monthly'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleCancelSubscription}
                      disabled={cancelingSubscription}
                      variant="outline"
                      className="rounded-xl text-red-600 border-red-300 hover:bg-red-50"
                    >
                      {cancelingSubscription ? 'Canceling...' : 'Cancel Subscription'}
                    </Button>
                  </div>
                </div>
              )}
              <Button
                data-testid="save-profile-btn"
                type="submit"
                className="rounded-xl bg-blue-600 hover:bg-blue-700"
              >
                Save Changes
              </Button>
            </form>
          ) : (
            user.bio && (
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">About</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{user.bio}</p>
              </div>
            )
          )}
        </div>

        {/* Earnings Overview - Only visible to profile owner */}
        {isOwnProfile && salesData && salesData.sales_count > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              Your Earnings
            </h2>
            
            {/* Payout Setup Warning */}
            {stripeStatus && !stripeStatus.onboarding_complete && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900 mb-1">Ready to receive your earnings?</p>
                    <p className="text-sm text-amber-800 mb-3">
                      You've earned ${salesData.total_revenue.toFixed(2)} from {salesData.sales_count} {salesData.sales_count === 1 ? 'sale' : 'sales'}. 
                      Complete your payout setup to receive these funds.
                    </p>
                    <Button
                      onClick={handleSetupPayout}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Setup Payouts Now
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
              <p className="text-3xl font-bold text-green-800 mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>
                ${salesData.total_revenue.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">{salesData.sales_count} {salesData.sales_count === 1 ? 'sale' : 'sales'} · You keep 100% of your listed price</p>
            </div>
          </div>
        )}

        {/* My Study Documents - Only visible to profile owner */}
        {isOwnProfile && studyDocs.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                My Study Documents
              </h2>
              <Button
                data-testid="go-to-study-builder-btn"
                onClick={() => navigate('/study-builder')}
                variant="outline"
                className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                size="sm"
              >
                <Brain className="w-4 h-4 mr-2" /> Study Builder
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {studyDocs.map(doc => (
                <div
                  key={doc.id}
                  data-testid={`study-doc-profile-${doc.id}`}
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gradient-to-r from-indigo-50/50 to-blue-50/50 hover:shadow-md transition-all"
                >
                  <div className="w-11 h-11 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{doc.title}</p>
                    <p className="text-xs text-gray-500 truncate">{doc.file_name}</p>
                  </div>
                  <Button
                    data-testid={`profile-study-btn-${doc.id}`}
                    onClick={() => navigate(`/study/${doc.id}`)}
                    size="sm"
                    className="rounded-lg bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
                  >
                    <Brain className="w-4 h-4 mr-1" /> Study
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Course Study Hubs - Only visible to profile owner */}
        {isOwnProfile && courses.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-700" />
                <h2 className="text-2xl font-semibold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                  Course Study Hubs
                </h2>
              </div>
              <Button
                data-testid="view-all-courses-btn"
                onClick={() => navigate('/courses')}
                variant="outline"
                size="sm"
                className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Browse All
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {courses.map((course) => (
                <Link
                  key={course.id}
                  to={`/course/${course.slug}`}
                  data-testid={`profile-course-${course.slug}`}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 hover:shadow-md transition-all border border-gray-200"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-800 truncate">{course.code ? `${course.code} - ${course.name}` : course.name}</p>
                    <p className="text-xs text-gray-500">{course.subject} &middot; {course.document_count} {course.document_count === 1 ? 'doc' : 'docs'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
            {isOwnProfile ? 'My Documents' : 'Documents'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {documents.map((doc) => (
              <Link
                key={doc.id}
                to={`/document/${doc.id}`}
                data-testid={`profile-document-${doc.id}`}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {doc.document_type}
                  </span>
                  <span className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Crimson Text, serif' }}>
                    ${doc.price.toFixed(2)}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {doc.title}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{doc.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{doc.purchase_count} purchases</span>
                  {doc.average_rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-medium text-gray-700">{doc.average_rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
          {documents.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No documents yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
